import { showDialog } from '@wwe-portal/ui/components/dialog';
import { LoginDialogPortable } from '@wwe-portal/web/components/auth/login-dialog';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { SyntheticEvent } from 'react';
import { useLimitedEventV2 } from '../rbe/use-limited-events';
import { useOfferPreviewDialog } from './use-offer-preview-dialog';
import { useXsollaPayment } from '../payment/use-xsolla-payment';
import { useGetPaymentContext } from './use-get-payment-context';
import {
  paymentScreenIsClosed,
  sendOfferPurchaseEvent,
  sendRewardsAnalyticsEvent,
} from '@wwe-portal/web/libs/analytics/offer';

import { STORAGE_ITEM_XSOLLA_SPECIAL_OFFER_UPSELL_OFFER } from '@wwe-portal/web/utils/constants/keys';
import { useUpsellOffer } from '@wwe-portal/web/components/interstitial/use-upsell-offer';

import { useNavigationState } from './category-navigation-state';
import { useClaimReward } from '../payment/use-claim-reward';
import {
  isFreeClaimOffer,
  isStandardOffer,
} from '@wwe-portal/web/components/stepup-offers/lib/mapper';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { getCachedVirtualizer } from './use-cached-virtualItems';
import { useTranslations } from 'next-intl';
import { isDripOffer, isOneTimeOffer } from '@wwe-portal/web/utils/offer';

type CustomSyntheticEvent = SyntheticEvent & {
  isCancelledClickOffer?: boolean;
};

export const useOfferHandler = (
  source: string,
  options: {
    getOfferById: (id: string) => OfferData | undefined;
    onClose?: () => void;
  }
) => {
  const t = useTranslations();
  const isAuthenticated = useIsAuthenticated();
  const { soloEvents = [] } = useLimitedEventV2();
  const { showPreview, closePreview } = useOfferPreviewDialog({
    onStartPayment: (payload) => {
      handleProcessOffer(payload.offerData);
    },
    onClose: options?.onClose,
  });

  const { handleUpsell } = useUpsellOffer({
    storageKey: STORAGE_ITEM_XSOLLA_SPECIAL_OFFER_UPSELL_OFFER,
  });

  const { startPayment } = useXsollaPayment(source, {
    onPaymentSuccess: (data) => {
      closePreview(data.offerData);
      sendOfferPurchaseEvent(data, {
        currencies: data.context.currencies,
        balance: data.context.balance,
        currentUser: data.context.currentUser,
      });
    },
    onCloseSuccessDialog: (data) => {
      paymentScreenIsClosed({
        offerData: data.offerData,
        analyticsData: {
          screen_category: data.context.screenCategory ?? '',
        },
      });
      if (data.context.upsellData) handleUpsell(data.context.upsellData);
    },
    onDescribePayment: (offer) => {
      if (isDripOffer(offer)) {
        return {
          title: t('payment_title_successful'),
          description: t('payment_description_successful_drip'),
        };
      } else {
        return undefined;
      }
    },
  });

  const { startClaimReward, isPending: isClaiming } = useClaimReward({
    onClaimSuccess: (data, context) => {
      closePreview(data.offerData);
      sendRewardsAnalyticsEvent(data, {
        currentUser: context.currentUser,
      });
    },
  });

  const getPaymentContext = useGetPaymentContext();

  const handleSelectById = (id: string) => {
    const foundOffer = options.getOfferById(id);
    const firstPointRuleEvent = soloEvents?.[0]?.pointRules?.[0];
    if (foundOffer) {
      showPreview({
        offerData: foundOffer,
        pointRule: firstPointRuleEvent,
      });
    }
  };

  const handleProcessOffer = (offerData: OfferData) => {
    if (!isAuthenticated) {
      showDialog(LoginDialogPortable, { title: 'Login To Make A Purchase' });
      return;
    }

    // Enable initial offset to cache the scroll position
    getCachedVirtualizer().getState().setEnabledInitialOffset(true);

    const context = getPaymentContext(
      offerData,
      useNavigationState.getState().activeId
    );
    if (isFreeClaimOffer(offerData)) {
      startClaimReward({ offerData, context });
      return;
    }

    if (isStandardOffer(offerData) || isOneTimeOffer(offerData)) {
      startPayment({
        offerData,
        context,
      });
    }
  };

  const handleClickOffer = (id: string, e: CustomSyntheticEvent | null) => {
    if (e && e['isCancelledClickOffer']) return;
    handleSelectById(id);
    // viewInterstitial();
  };

  const handleDirectPurchase = (id: string, e: CustomSyntheticEvent | null) => {
    if (!isAuthenticated) {
      if (e) {
        e['isCancelledClickOffer'] = true;
      }
      showDialog(LoginDialogPortable, { title: 'Login To Make A Purchase' });
      return;
    }
    const foundOffer = options.getOfferById(id);

    handleSelectById(id);

    if (foundOffer) {
      handleProcessOffer(foundOffer);
    }
  };

  return {
    onClick: handleClickOffer,
    onClickPrice: handleDirectPurchase,
    isClaiming,
  };
};
